import { humanise } from 'module/ui/components/atoms/humanised-date';
import {
  HeadingCompact01,
  HeadingCompact02,
  Short00,
  Short01,
} from 'module/ui/components/styles/typography';
import { SignalOccurrenceGroupedForUser } from 'api/account-people/signal-occurrence-types';
import { SignalType } from 'api/signal/types';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { WidthTransition } from 'styles/common';
import { HeaderTag } from './header-tag';
import { TextWithCitations } from '../../text-with-citations';

const Wrapper = styled.div`
  display: flex;
  gap: var(--spacing-05);
`;
const SignalContentHeader = styled.div`
  ${HeadingCompact02};
`;
const SignalContentDescriptionHeading = styled.div`
  ${HeadingCompact01};
`;
const SignalContentDescription = styled.div``;
const SignalContentCard = styled.div<{ $isFullWidth?: boolean }>`
  padding: var(--spacing-05);
  height: auto;
  ${WidthTransition};
  ${({ $isFullWidth }) =>
    $isFullWidth
      ? css`
          width: 60%;
        `
      : css`
          width: 560px;
        `};
  border: 1px solid var(--divider-default);
  border-radius: var(--border-radius-small);
  box-shadow: var(--shadow-02);
  ${SignalContentHeader},
  ${SignalContentDescription} {
    margin-top: var(--spacing-02);
  }
  ${SignalContentDescriptionHeading} {
    margin-top: var(--spacing-03);
  }
`;
const TeamResponseCard = styled.div<{ $isFullWidth?: boolean }>`
  padding: var(--spacing-05);
  height: auto;
  ${WidthTransition};
  ${({ $isFullWidth }) =>
    $isFullWidth
      ? css`
          width: 40%;
        `
      : css`
          width: 246px;
        `};
  background-color: var(--scarlet-gray-20);
  border-radius: var(--border-radius-small);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-07);
  box-shadow: var(--shadow-02);
`;

const TeamResponseHeader = styled.div<{ subtle?: boolean }>`
  ${({ subtle }) =>
    subtle
      ? css`
          ${Short00};
          color: var(--text-tertiary);
        `
      : css`
          ${HeadingCompact01};
        `};
`;
const TeamResponseContent = styled.div`
  ${Short01};
`;
const TeamResponseEmpty = styled(TeamResponseContent)`
  color: var(--text-tertiary);
`;

const TeamResponseDate = styled.div`
  ${Short00};
  color: var(--text-tertiary);
`;
const TeamResponseBlock = styled.div`
  display: flex;
  flex-direction: column;
  ${TeamResponseContent} {
    margin-top: var(--spacing-02);
  }
  ${TeamResponseDate} {
    margin-top: var(--spacing-03);
  }
`;

interface Props {
  signalOccurrence: SignalOccurrenceGroupedForUser;
  signalType: SignalType;
  isFullWidth?: boolean;
}
export const SignalOccurrence = ({
  signalOccurrence,
  signalType,
  isFullWidth,
}: Props) => {
  const { teamResponses } = signalOccurrence;
  const teamResponseEmpty = teamResponses.length === 0;
  const latestResponse = teamResponseEmpty ? null : teamResponses[0];
  const oldResponse = teamResponseEmpty
    ? null
    : teamResponses.length > 1
      ? teamResponses[teamResponses.length - 1]
      : null;

  return (
    <Wrapper>
      <SignalContentCard $isFullWidth={isFullWidth}>
        <HeaderTag
          signalType={signalType}
          signalOccurrence={signalOccurrence}
        />
        <SignalContentHeader>{signalOccurrence.signalName}</SignalContentHeader>
        <SignalContentDescriptionHeading>
          <FormattedMessage id="account.people.signal_occurrence.description" />
        </SignalContentDescriptionHeading>
        <SignalContentDescription>
          <TextWithCitations
            value={signalOccurrence.description}
            citationMapping={signalOccurrence.citationMapping}
          />
        </SignalContentDescription>
      </SignalContentCard>
      {latestResponse ? (
        <TeamResponseCard $isFullWidth={isFullWidth}>
          <TeamResponseBlock>
            <TeamResponseHeader>
              {signalType === SignalType.Risk ? (
                <FormattedMessage id="account.people.current_risks.latest_response.title" />
              ) : (
                <FormattedMessage id="account.people.opportunity_log.latest_response.title" />
              )}
            </TeamResponseHeader>
            <TeamResponseContent>
              <TextWithCitations
                value={latestResponse.response}
                citationMapping={latestResponse.citationMapping}
              />
            </TeamResponseContent>
            <TeamResponseDate>
              <FormattedMessage
                id="account.people.latest_response.date"
                values={{
                  date: humanise(moment(latestResponse.respondedAt)),
                }}
              />
            </TeamResponseDate>
          </TeamResponseBlock>
          {oldResponse && (
            <TeamResponseBlock>
              <TeamResponseHeader subtle>
                <FormattedMessage id="account.people.signal_occurrence.old_response.title" />
              </TeamResponseHeader>
              <TeamResponseContent>
                <TextWithCitations
                  value={oldResponse.response}
                  citationMapping={oldResponse.citationMapping}
                />
              </TeamResponseContent>
            </TeamResponseBlock>
          )}
        </TeamResponseCard>
      ) : (
        <TeamResponseCard>
          <TeamResponseBlock>
            <TeamResponseHeader>
              {signalType === SignalType.Risk ? (
                <FormattedMessage id="account.people.current_risks.latest_response.title" />
              ) : (
                <FormattedMessage id="account.people.opportunity_log.latest_response.title" />
              )}
            </TeamResponseHeader>
            <TeamResponseEmpty>
              <FormattedMessage id="account.people.signal_occurrence.no_response" />
            </TeamResponseEmpty>
          </TeamResponseBlock>
        </TeamResponseCard>
      )}
    </Wrapper>
  );
};
